import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslocoService } from '@jsverse/transloco';
import { toShowModalsConditionMap } from '../../app/core/constants/base-error-messages.constants';
import { ERROR_CONTEXT_HEADER, ERROR_CONTEXT_ALARMS } from '@data';

/**
 * Error Interceptor
 *
 * Observe the errors that occurred in the service and display notifications.
 */
export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
  const notificationService = inject(NzNotificationService);
  const translocoService = inject(TranslocoService);

  return next(req).pipe(
    catchError((error) => {
      handleHttpError(error, notificationService, translocoService, req);
      return throwError(() => error);
    }),
  );
};

/**
 * Handle the error of the services.
 *
 * @param error - HTTP error object
 * @param notificationService - Service to show notifications
 * @param translocoService - Service for translations
 * @param req - HTTP request object to extract context
 */
function handleHttpError(
  error: any,
  notificationService: NzNotificationService,
  translocoService: TranslocoService,
  req: HttpRequest<any>,
): void {
  // Skip 404 errors - don't show notifications for them
  if (error.status === 404) {
    return;
  }

  const errorMessage = error.error?.message?.toLowerCase() || '';

  // Get the match of the message of the error with the map of the conditions to show the notification.
  const matchMessage = Array.from(toShowModalsConditionMap.values()).some(
    (sentence) => errorMessage.includes(sentence.toLowerCase()),
  );

  // Get the key of the value match with the searchValue.
  const keyMessage = getKeyByValueCaseInsensitive(
    toShowModalsConditionMap,
    errorMessage,
  );

  // Show the notification if there is a match.
  if (matchMessage) {
    // Extract context from request headers or URL
    const contextTitle =
      req.headers.get(ERROR_CONTEXT_HEADER) || 'NOTIFICATIONS.GENERAL.TITLE';

    // Fix: Handle null keyMessage to avoid "ERROR.null"
    const errorKey = keyMessage ? `ERROR.${keyMessage}` : 'ERROR.UNKNOWN';

    showErrorNotification(
      notificationService,
      translocoService,
      contextTitle,
      errorKey,
    );
  }
}

/**
 * Retrieves the key from a map based on a case-insensitive match with.
 *
 * @param map - Map of optional string representing key word and the message to match.
 * @param searchValue - Text to match with the value of the Map.
 * @returns Key of the value match with the serchValue.
 */
function getKeyByValueCaseInsensitive(
  map: Map<string, string>,
  searchValue: string,
) {
  for (let [key, value] of map) {
    // Use includes() logic to match the same way as matchMessage check
    if (searchValue.includes(value.toLowerCase())) {
      return key;
    }
  }
  return null;
}

/**
 * Function to show the error notification.
 *
 * @param notificationService - Service to display notifications
 * @param translocoService - Service for translations
 * @param title - Translation key for the notification title
 * @param message - Translation key for the notification message
 */
function showErrorNotification(
  notificationService: NzNotificationService,
  translocoService: TranslocoService,
  title: string,
  message: string,
): void {
  notificationService.create(
    'error',
    translocoService.translate(title),
    translocoService.translate(message),
    {
      nzPlacement: 'bottomRight',
    },
  );
}
